import type { InsightFamily } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { getInsightFamilyFilterQueryExpression } from "../../utils/insightFamilies.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export const DEFAULT_GET_ALL_FAMILIES_PAGE_SIZE = 50;

export interface AllInsightFamiliesActionResponse {
    data: InsightFamily[];
    insightsCount: number;
}
export const emptyResponse: AllInsightFamiliesActionResponse = {
    data: [],
    insightsCount: 0,
};

export interface AllInsightFamiliesActionProps {
    advertiserId: string;
    marketplaceId: string;
}

export function useGetAllInsightFamiliesAsync({
    advertiserId,
    marketplaceId,
}: AllInsightFamiliesActionProps): [
    AsyncState<AllInsightFamiliesActionResponse>,
    UseAsyncActions<AllInsightFamiliesActionResponse, [number]>,
] {
    const request = {
        advertiserId,
        marketplaceId,
        pageSize: DEFAULT_GET_ALL_FAMILIES_PAGE_SIZE,
        queryExpression: getInsightFamilyFilterQueryExpression(),
    };

    const [uatStandalone] = useStore(FeatureFlagsStore, (s) => [
        s.uatStandalone,
    ]);

    const [insightsState, insightsAction] = useAsync(
        async (pageNumber: number) => {
            const response = await (
                await getCdbApiClient()
            ).getInsightFamiliesV2({
                ...request,
                pageNumber,
            });

            if (!uatStandalone)
                response.data = response.data?.filter(
                    (insight) => !insight.familyName?.endsWith("Uat"),
                );

            // filter out insight family standalone uat insights by default
            return {
                ...emptyResponse,
                ...response,
            } as AllInsightFamiliesActionResponse;
        },
        emptyResponse,
    );

    useAsyncErrorStatusNotification(insightsState);

    return [insightsState, insightsAction];
}
